import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { SplashComponent } from './framework/splash/splash.component';
import { FullPageComponent } from './layout/full-page/full-page.component';
import { routerSegments } from '@consts/routes';
import { LocationsResolver } from './locations/locations.resolver';
import { AuthenticationGuardService } from './core/authentication-guard.service';
import { SidebarV2Component } from './layout/sidebar-v2/sidebar-v2.component';
import { isDefaultOrgGuard } from './guards/is-default-org.guard';
import { RouterPermissionGuard } from './guards/router-permission-guard';
import { PermissionModel } from '@models/permission.model';
import { SensorsViewComponent } from './pages/sensors-view/sensors-view.component';
import { userRoleGuard } from './guards/user-role.guard';
import { operatorRoleGuard } from './guards/operator-role.guard';
import { AlarmsManagementComponent } from './pages/alarm-mangment/alarms-management.component';
import { EditLocationRoutes } from './pages/edit-location/edit-location.routes';
import { databaseRoutes } from './pages/database/database.routes';
import { MapComponent } from './pages/map/map.component';
import { WorkflowComponent } from './pages/workflow/workflow.component';
import { WorkflowsTableComponent } from './pages/workflow/components/workflows-table/workflows-table.component';
import { RunListPageComponent } from './shared/flow-chart/components/run-list-page/run-list-page.component';
import { RunStepsPageComponent } from './shared/flow-chart/components/run-steps-page/run-steps-page.component';
import { DeveloperGuard } from './guards/developer.guard';
import { cblEnabledGuard } from './guards/cbl-enabled.guard';
import { chatMatrixFeatureGuard } from './guards/chat-matrix-feature.guard';
import { wallV3PageRedirectGuard } from './guards/wall-v3-page-redirect.guard';

const authenticatedRoutes: Routes = [
  {
    path: 'home',
    children: [
      {
        path: '**',
        redirectTo: `/${routerSegments.locationV2}/list`,
      },
    ],
  },
  {
    path: routerSegments.alertsV2,
    loadChildren: () => import('./pages/alerts-v2/alerts-v2.module').then(m => m.AlertsV2Module),
  },
  {
    path: 'search',
    canActivate: [RouterPermissionGuard],
    data: {
      permissions: [PermissionModel.Permissions.SearchView],
    },
    loadChildren: () => import('./pages/search/search.module').then(m => m.SearchModule),
  },
  {
    path: 'organization',
    loadChildren: () => import('./organization/organization.module').then(m => m.OrganizationModule),
  },
  {
    path: routerSegments.locations,
    loadChildren: () => import('./locations/locations.module').then(m => m.LocationsModule),
  },
  {
    path: 'cameras',
    loadChildren: () => import('./cameras/cameras.module').then(m => m.CamerasModule),
  },
  {
    path: 'camera',
    loadChildren: () => import('./pages/camera/camera.module').then(m => m.CameraModule),
  },
  {
    path: routerSegments.wallV2,
    canActivate: [wallV3PageRedirectGuard],
    loadChildren: () => import('./pages/wall-v2/wall-v2.module').then(m => m.WallV2Module),
  },
  {
    path: routerSegments.wallV3,
    canActivate: [wallV3PageRedirectGuard],
    loadChildren: () => import('./pages/wall-v3/wall-v3.routes').then(m => m.wallV3Routes),
  },
  {
    path: routerSegments.alarmManagement,
    component: AlarmsManagementComponent,
  },
  // {
  //   path: routerSegments.alarmsManager,
  //   component: AlarmsManagerComponent,
  // },
  {
    path: `${routerSegments.sensors}/:locationId/:sensorId`,
    loadComponent: () => SensorsViewComponent,
  },
  {
    path: routerSegments.operator,
    canActivate: [operatorRoleGuard],
    loadChildren: () => import('./operator/operator.module').then(m => m.OperatorModule),
  },
  {
    path: routerSegments.cameraEdit,
    loadChildren: () => import('./pages/camera-edit/camera-edit.module').then(m => m.CameraEditModule),
  },
  {
    path: routerSegments.edgeEdit,
    loadChildren: () => import('./pages/edge-edit/edge-edit.module').then(m => m.EdgeEditModule),
  },
  {
    path: routerSegments.editLocation,
    children: EditLocationRoutes,
  },
  {
    path: routerSegments.archive,
    loadChildren: () => import('./pages/archive/archive.routes').then(m => m.archiveRoutes),
  },
  {
    path: routerSegments.account,
    loadChildren: () => import('./pages/account/account.routes').then(m => m.accountRoutes),
  },
  {
    path: routerSegments.settings,
    loadChildren: () => import('./pages/settings/setting.routes').then(m => m.settingRoutes),
  },
  {
    path: routerSegments.database,
    loadChildren: () => databaseRoutes,
  },
  {
    path: routerSegments.dashboards,
    canActivate: [RouterPermissionGuard],
    data: {
      permissions: [PermissionModel.Permissions.DashboardView],
    },
    loadChildren: () => import('./pages/dashboards/dashboards.routes').then(m => m.dashboardRoutes),
  },
  {
    path: routerSegments.pricing,
    loadChildren: () => import('./pages/pricing/pricing.module').then(m => m.PricingModule),
  },
  {
    path: routerSegments.reports,
    loadChildren: () =>
      import('./pages/reports/reports.routes').then(m => m.reportsRoutes),
  },
  {
    path: routerSegments.cases,
    canActivate: [RouterPermissionGuard],
    data: {
      permissions: [PermissionModel.Permissions.CaseManage],
    },
    loadChildren: () =>
      import('./pages/case-management/case-management.routes').then(m => m.caseManagementRoutes),
  },
  {
    path: routerSegments.chat,
    canActivate: [chatMatrixFeatureGuard],
    loadChildren: () => import('@share-chat').then((m) => m.libChatRoutes),
  },
  {
    path: routerSegments.accessControlEvents,
    loadChildren: () => import('./pages/control-events/control-events.routes').then(m => m.ControlEventsRoutes),
  },
  {
    path: routerSegments.map,
    loadComponent: () => MapComponent,
  },
  {
    path: routerSegments.help,
    loadChildren: () => import('./pages/help/help.module').then(m => m.HelpModule),
  },
  {
    path: routerSegments.locationV2,
    canActivate: [userRoleGuard],
    loadChildren: () => import('./pages/location/location.module').then(m => m.LocationModule),
  },
  {
    path: routerSegments.monitoring,
    loadChildren: () => import('./pages/monitoring/monitoring.routes').then(m => m.monitoringRoutes)
  },
  {
    path: routerSegments.support,
    loadChildren: () => import('./pages/support/support.module').then(m => m.SupportModule),
  },
  {
    path: `${routerSegments.sensors}/:locationId/:sensorId`,
    loadComponent: () => SensorsViewComponent,
  },
  {
    path: `${routerSegments.editLocation}/:locationId/${routerSegments.sensors}/${routerSegments.view}/:sensorId`,
    loadComponent: () => SensorsViewComponent,
  },
  {
    path: routerSegments.operatorSettings,
    loadChildren: () => import('./pages/operator-settings/operator.routes').then(m => m.operatorRoutes),
  },
  {
    path: routerSegments.searchV2,
    canActivate: [RouterPermissionGuard],
    data: {
      permissions: [PermissionModel.Permissions.SearchView],
    },
    loadChildren: () =>
      import('./pages/search-v2/search.routes')
        .then(m => m.searchRoutes),
  },
  {
    path: routerSegments.partnerPortal,
    loadChildren: () => import('./pages/partner-portal/partner-portal.module').then(m => m.PartnerPortalModule),
  },
  {
    path: routerSegments.managementPortal,
    loadChildren: () => import('./pages/partner-portal/partner-portal.module').then(m => m.PartnerPortalModule),
  },
  {
    path: routerSegments.locationV3,
    loadChildren: () =>
      import('./pages/location-v2/location.routes')
        .then(m => m.locationRoutes),
  },
  {
    path: routerSegments.adminSettings,
    loadChildren: () => import('./pages/admin-settings/admin-settings.routes').then(m => m.adminSettingsRoutes),
  },
  {
    path: `${routerSegments.workflows}/${routerSegments.create}`,
    loadComponent: () =>  WorkflowComponent,
    canActivate: [DeveloperGuard],
  },
  {
    path: `${routerSegments.workflows}/:id`,
    loadComponent: () =>  WorkflowComponent,
    canActivate: [DeveloperGuard],
  },
  {
    path: routerSegments.workflows,
    loadComponent: () =>  WorkflowsTableComponent,
    canActivate: [DeveloperGuard],
  },
  {
    path: routerSegments.advancedAnalyticsJobs,
    loadComponent: () =>
      import('./pages/advanced-analytics-jobs/advanced-analytics-jobs.component')
        .then(m => m.AdvancedAnalyticsJobsComponent),
    canActivate: [cblEnabledGuard, RouterPermissionGuard],
    data: {
      // Either permission grants the page; the tabs inside are gated individually.
      permissions: [
        PermissionModel.Permissions.AdvancedAnalyticsJobsRead,
        PermissionModel.Permissions.AdvancedAnalyticsJobsManage,
      ],
    },
  },
  {
    path: `${routerSegments.workflows}/:graphId/${routerSegments.runs}`,
    loadComponent: () =>  RunListPageComponent,
    canActivate: [DeveloperGuard],
  },
  {
    path: `${routerSegments.workflows}/:graphId/${routerSegments.runs}`,
    loadComponent: () =>  RunListPageComponent,
    canActivate: [DeveloperGuard],
  },
  {
    path: `${routerSegments.workflows}/:graphId/${routerSegments.runs}/:runId/${routerSegments.steps}`,
    loadComponent: () =>  RunStepsPageComponent,
    canActivate: [DeveloperGuard],
  },
  {
    path: '**',
    redirectTo: `${routerSegments.locationV2}/${routerSegments.home}`,
  },
];

const routes: Routes = [
  {
    path: routerSegments.noAuth,
    loadChildren: () => import('./pages/no-auth/no-auth.module').then(m => m.NoAuthModule),
  },
  {
    path: routerSegments.notFound,
    loadChildren: () => import('./pages/not-found/not-found.routes').then(m => m.notFoundRoutes),
  },
  {
    path: routerSegments.auth,
    loadChildren: () => import('./pages/auth/auth.routes').then(m => m.authRoutes),
  },
  {
    path: routerSegments.emailVerification,
    redirectTo: `${routerSegments.public}/${routerSegments.emailVerification}`,
    pathMatch: 'full',
  },
  {
    path: routerSegments.logout,
    component: FullPageComponent,
  },
  {
    path: `${routerSegments.grantedAccess}`,
    loadChildren: () => import('./pages/granted-access/granted-access.module').then(m => m.GrantedAccessModule),
  },
  {
    path: `${routerSegments.alertVideo}`,
    loadChildren: () => import('./pages/alert-video/alert-video.module').then(m => m.AlertVideoModule),
  },
  {
    path: 'splash',
    component: FullPageComponent,
    pathMatch: 'full',
    children: [
      {
        path: '',
        component: SplashComponent,
      },
    ],
  },
  {
    path: routerSegments.terms,
    loadChildren: () => import('./pages/terms/terms.module').then(m => m.TermsModule),
  },
  {
    path: routerSegments.privacy,
    loadChildren: () => import('./pages/privacy/privacy.module').then(m => m.PrivacyModule),
  },
  {
    path: routerSegments.selectOrganization,
    loadChildren: () => import('./pages/organization-layout/organization-layout.routes').then(m => m.orgLayoutRoutes),
    canActivate: [isDefaultOrgGuard],
  },
  {
    path: routerSegments.public,
    loadChildren: () => import('./pages/public/public.routes').then(m => m.publicRoutes),
  },
  {
    path: '',
    children: authenticatedRoutes,
    component: SidebarV2Component,
    canActivate: [AuthenticationGuardService],
    resolve: {
      // userProfileResolver: UserResolver,
      locationResolver: LocationsResolver,
    },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
